/**
 * De onde vem o dado de motor de um veículo. O backend decide e manda em
 * `engine_data_level`; o front não recalcula.
 *
 * - `ignition`: só o sinal de ignição do rastreador.
 * - `engine_wire`: um fio de entrada do rastreador ligado ao motor.
 * - `can`: o barramento CAN do veículo.
 */
export type EngineDataLevel = "ignition" | "engine_wire" | "can";

export interface RpmZones {
  green: number;
  yellow: number;
  red: number;
  idle: number;
}

/** O subconjunto de `timings` de que a decisão de nível precisa. */
export interface EngineTimings {
  stopped_with_ignition_on: number;
  engine_on?: number;
  engine_idle?: number;
  rpm?: RpmZones;
}

export const ENGINE_ON_LABEL = "Motor ligado";
export const IDLE_LABEL = "Motor ocioso";

/**
 * Lê o `engine_data_level` do payload.
 *
 * O front pode estar falando com uma API anterior à Fase 1, que não manda o
 * campo. Nesse caso `has_engine_on_info` — que continua saindo em todos os
 * payloads — é o melhor palpite: quem tem o fio é `engine_wire`, quem não tem
 * é `ignition`. Nunca `can`, porque não havia como saber.
 */
export function parseEngineDataLevel(
  raw: unknown,
  hasEngineOnInfo = false,
): EngineDataLevel {
  if (raw === "can" || raw === "engine_wire" || raw === "ignition") {
    return raw;
  }
  return hasEngineOnInfo ? "engine_wire" : "ignition";
}

/**
 * No nível `ignition` não existe tempo de motor ligado para mostrar — a
 * ignição ligada não prova que o motor está girando.
 */
export function showsEngineOn(level: EngineDataLevel): boolean {
  return level !== "ignition";
}

/**
 * No nível `ignition` o ocioso é o tempo parado com ignição ligada: uma
 * estimativa boa, mas estimativa.
 */
export function idleIsEstimated(level: EngineDataLevel): boolean {
  return level === "ignition";
}

export function hasRpmRanges(level: EngineDataLevel): boolean {
  return level === "can";
}

/**
 * O tempo ocioso deste veículo, na fonte que o nível dele determina.
 *
 * Um veículo tem exatamente um tempo ocioso. `null` significa que ele não tem
 * essa informação neste período — a tela mostra um travessão, porque um
 * `00:00` seria lido como "nunca ficou ocioso", que é coisa diferente.
 */
export function idleSeconds(
  level: EngineDataLevel,
  timings: EngineTimings,
): number | null {
  switch (level) {
    case "ignition":
      // A chave ligada com o motor desligado conta junto. É estimativa, mas
      // corresponde à realidade na esmagadora maioria dos casos.
      return timings.stopped_with_ignition_on;
    case "engine_wire":
      return timings.engine_idle ?? null;
    case "can":
      return timings.rpm?.idle ?? null;
  }
}

/**
 * O tempo de motor ligado. `null` no nível `ignition`, onde não existe.
 *
 * No nível `can` a fonte é o próprio barramento — `green + yellow + red +
 * idle` — e não o fio, mesmo quando o veículo também tem o fio.
 */
export function engineOnSeconds(
  level: EngineDataLevel,
  timings: EngineTimings,
): number | null {
  switch (level) {
    case "ignition":
      return null;
    case "engine_wire":
      return timings.engine_on ?? null;
    case "can": {
      const rpm = timings.rpm;
      if (!rpm) return null;
      return rpm.green + rpm.yellow + rpm.red + rpm.idle;
    }
  }
}

export interface EngineColumn {
  key: string;
  label: string;
  seconds: (timings: EngineTimings) => number | null;
}

/**
 * As colunas de motor de uma tabela, na ordem em que aparecem.
 *
 * O nível `ignition` tem uma só — o ocioso estimado. Os outros dois têm duas.
 * A coluna "Parado c/ ign." não existe em nenhum nível: no `ignition` ela *é*
 * o ocioso e aparece sob esse rótulo; nos outros ela seria um segundo tempo de
 * ociosidade na mesma linha, que é exatamente o que este trabalho corta.
 */
export function engineColumns(level: EngineDataLevel): EngineColumn[] {
  const idle: EngineColumn = {
    key: "idle",
    label: IDLE_LABEL,
    seconds: (timings) => idleSeconds(level, timings),
  };
  if (!showsEngineOn(level)) {
    return [idle];
  }
  return [
    {
      key: "engine_on",
      label: ENGINE_ON_LABEL,
      seconds: (timings) => engineOnSeconds(level, timings),
    },
    idle,
  ];
}
